// Minimal SSE client backed by fetch + ReadableStream.
// (EventSource doesn't support POST / cookies in some setups, so we roll our own.)

import type { Citation } from "./client";

export type PhaseName = "retrieving" | "retrieved" | "generating";

export type SseEvent =
  | { type: "thread"; thread_id: number }
  | { type: "phase"; name: PhaseName; count?: number }
  | { type: "retrieved"; citations: Citation[] }
  | { type: "token"; text: string }
  | { type: "thinking"; text: string }
  | { type: "usage"; usage: { input_tokens?: number; output_tokens?: number; cache_read_input_tokens?: number } }
  | { type: "done" }
  | { type: "error"; detail: string };

export async function* postSseStream(url: string, body: object, signal?: AbortSignal): AsyncGenerator<SseEvent> {
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    credentials: "include",
    signal,
  });
  if (!resp.ok) {
    const detail = await resp.text();
    throw new Error(`HTTP ${resp.status}: ${detail || resp.statusText}`);
  }
  if (!resp.body) throw new Error("missing response body");

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIdx;
    while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      const event = parseEvent(raw);
      if (event) yield event;
    }
  }
}

function parseEvent(raw: string): SseEvent | null {
  let event: string | null = null;
  let data = "";
  for (const line of raw.split("\n")) {
    if (!line) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
  }
  if (event === null) return null;

  switch (event) {
    case "thread":
      return { type: "thread", ...JSON.parse(data) };
    case "phase":
      return { type: "phase", ...JSON.parse(data) };
    case "retrieved":
      return { type: "retrieved", citations: JSON.parse(data) };
    case "token":
      // token data is plain text JSON-encoded as a string
      return { type: "token", text: tryJsonString(data) };
    case "thinking":
      return { type: "thinking", text: tryJsonString(data) };
    case "usage":
      return { type: "usage", usage: JSON.parse(data) };
    case "done":
      return { type: "done" };
    case "error":
      return { type: "error", detail: JSON.parse(data).detail ?? "unknown" };
    default:
      return null;
  }
}

function tryJsonString(raw: string): string {
  try {
    const v = JSON.parse(raw);
    return typeof v === "string" ? v : raw;
  } catch {
    return raw;
  }
}
